import { defineStore } from 'pinia'
import axios from '@/config/axios'
import { isAxiosError, type AxiosResponse } from 'axios'
import { usePortfolioStore } from './portfolioStore'
// Types pour les paramètres et les données
interface DividendParams {
  page?: number;
  page_size?: number;
  portfolio_id?: number;
  date_from?: string;
  date_to?: string;
  asset?: number;
  amount_min?: number;
  amount_max?: number;
  ordering?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}
interface Dividend {
  id: number;
  asset: number;
  asset_name?: string;
  asset_symbol?: string;
  portfolio: number;
  portfolio_name?: string;
  date: string;
  amount: number;
  currency: string;
  tax_rate?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}
interface Pagination {
  count: number;
  next: string | null;
  previous: string | null;
  results: Dividend[];
}
interface CacheItem<T = unknown> {
  data: T;
  timestamp: number;
}
interface DividendAssetSummary {
  asset_name?: string
  asset_symbol?: string
  symbol?: string
  name?: string
  total_amount?: number
}
export interface DividendsSummary {
  total_amount: number
  top_asset: DividendAssetSummary | null
  bottom_asset: DividendAssetSummary | null
}
interface DividendByYear {
  year: number | string
  total_amount: number | string
}
interface DividendYieldByYear {
  year: number | string
  dividend_amount: number
  avg_portfolio_value: number
  dividend_yield: number
}
interface YearlyDividendByAsset {
  year: number
  asset_name?: string
  asset_symbol?: string
  symbol?: string
  name?: string
  total_amount: number
}
interface EligibleDividendAsset {
  id: number
  name: string
  symbol: string
  quantity?: number
}
interface MarketYieldByAssetYear {
  year: number
  asset_symbol?: string
  asset_name?: string
  market_yield?: number
}
interface PerShareDividendByYearAsset {
  year: number
  asset: { symbol: string; name: string }
  dividend_per_share_avg: number
}
type DividendCacheValue =
  | Pagination
  | Dividend[]
  | DividendByYear[]
  | DividendYieldByYear[]
  | YearlyDividendByAsset[]
  | MarketYieldByAssetYear[]
  | PerShareDividendByYearAsset[]
  | DividendsSummary
  | number
function isAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const code = (error as Error & { code?: string }).code
  return error.name === 'AbortError' || code === 'ECONNABORTED'
}
function getDividendErrorDetail(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    return (error.response?.data as { detail?: string } | undefined)?.detail ?? fallback
  }
  return fallback
}
// Payload utilisé pour la création d'un dividende (correspond au backend)
interface DividendCreatePayload {
  portfolio_id: number;
  asset_id: number;
  settlement_date: string; // date
  total_amount: number | null;
  quantity: number | null;
  price_per_unit: number | null;
  transaction_type_id: number; // 3 pour dividendes
}
// Interface supprimée pour améliorer les performances
interface DividendState {
  dividends: Dividend[];
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  cache: Record<string, CacheItem<DividendCacheValue>>;
  cacheTTL: number;
  maxCacheEntries: number;
  currentControllers: Record<string, AbortController>;
  // Contrôleur d'annulation centralisé pour fetchDividendsByPortfolio
  portfolioFetchController: AbortController | null;
  // États de chargement spécifiques pour chaque type de requête
  isLoadingDividends: boolean;
  isLoadingDividendsByPortfolio: boolean;
  isLoadingDividendTableData: boolean;
  isLoadingDividendsByYear: boolean;
  isLoadingTotalDividends: boolean;
  // Cache spécifique pour total_dividends par portefeuille
  _totalDividendsCache: Record<number, CacheItem<number>>;
  // Requêtes en cours pour total_dividends par portefeuille (dedup)
  _totalDividendsInFlight: Map<number, Promise<number>>;
}
// Utilitaire de normalisation des paramètres
function normalizeParams(params: DividendParams): Record<string, string | number> {
  // Mapping supprimé car les propriétés redondantes ont été retirées de l'interface
  const normalized: Record<string, string | number> = {}
  for (const key in params) {
    if (params[key as keyof DividendParams] !== undefined && params[key as keyof DividendParams] !== null) {
      // Plus besoin de conversion de noms de propriétés
      normalized[key] = params[key as keyof DividendParams]
    }
  }
  return normalized
}
export const useDividendStore = defineStore('dividend', {
  state: (): DividendState => ({
    dividends: [],
    loading: false,
    error: null,
    pagination: {
      count: 0,
      next: null,
      previous: null,
      results: []
    },
    cache: {},
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    maxCacheEntries: 100,
    currentControllers: {},
    // Contrôleur d'annulation centralisé pour fetchDividendsByPortfolio
    portfolioFetchController: null,
    // Initialisation des états de chargement spécifiques
    isLoadingDividends: false,
    isLoadingDividendsByPortfolio: false,
    isLoadingDividendTableData: false,
    isLoadingDividendsByYear: false,
    isLoadingTotalDividends: false,
    _totalDividendsCache: {},
    _totalDividendsInFlight: new Map<number, Promise<number>>()
  }),
  getters: {
    // Les getters liés aux métriques de performance ont été supprimés pour améliorer les performances
  },
  actions: {
    async fetchDividends(params: DividendParams = {}): Promise<Dividend[]> {
      // Conserver l'état précédent des dividendes pour éviter l'affichage vide temporaire
      const previousDividends = [...this.dividends]
      // Annuler toute requête précédente pour fetchDividends
      this._abortPreviousRequest('fetchDividends')
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController()
      this.currentControllers['fetchDividends'] = controller
      this.loading = true
      this.isLoadingDividends = true
      this.error = null
      // Variable pour suivre si la requête a été annulée
      let wasCancelled = false
      try {
        let url = '/dividends/'
        const queryParams = this._buildQueryParams(params)
        // Ajouter les paramètres à l'URL
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`
        }
        const response = await axios.get<Pagination>(url, {
          signal: controller.signal
        })
        // Mise à jour des données
        this.dividends = response.data.results || []
        this.pagination = response.data
        return this.dividends
      } catch (error: unknown) {
        // Vérifier si l'erreur est due à une annulation
        if (isAbortError(error)) {
          wasCancelled = true
          return previousDividends
        }
        this.error = getDividendErrorDetail(error, 'Erreur lors du chargement des dividendes')
        return []
      } finally {
        if (!wasCancelled) {
          this.loading = false
          this.isLoadingDividends = false
          delete this.currentControllers['fetchDividends']
        }
      }
    },
    // Fonction de base optimisée pour récupérer les dividendes avec des paramètres
    async _fetchDividendsBase(portfolioId: number, params: DividendParams = {}, externalSignal?: AbortSignal, action: string = 'base'): Promise<Pagination | Dividend[]> {
      // Vérifier d'abord si le portefeuille existe
      const portfolioStore = usePortfolioStore()
      // Si le portefeuille est déjà marqué comme inexistant, ne pas faire d'appel API
      if (portfolioStore.$state.notFoundPortfolios.has(portfolioId)) {
        this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
        throw new Error(`Portefeuille avec l'ID ${portfolioId} non trouvé`)
      }
      // Vérifier si le portefeuille existe dans le store
      if (!portfolioStore.getPortfolioById(portfolioId)) {
        try {
          // Tenter de charger le portefeuille
          await portfolioStore.fetchPortfolioDetails(portfolioId)
        } catch (err: unknown) {
          // Si le portefeuille n'existe pas, gérer proprement l'erreur
          if (isAxiosError(err) && err.response?.status === 404) {
            this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
            throw new Error(`Portefeuille avec l'ID ${portfolioId} non trouvé`)
          }
          throw err
        }
      }
      // Générer une clé unique pour cette requête
      const requestKey = `${action}_${portfolioId}_${JSON.stringify(params)}`
      // Annuler toute requête précédente pour cette action spécifique
      this._abortPreviousRequest(requestKey)
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController()
      this.currentControllers[requestKey] = controller
      // Si un signal externe est fourni, l'utiliser également
      if (externalSignal) {
        // Logique pour combiner les signaux supprimée pour simplifier
      }
      // Définir l'état de chargement approprié
      this.loading = true
      // Définir l'état de chargement spécifique en fonction de l'action
      switch (action) {
        case 'portfolio':
          this.isLoadingDividendsByPortfolio = true
          break
        case 'table':
          this.isLoadingDividendTableData = true
          break
        default:
          // Pour les autres actions, utiliser l'état de chargement général
          break
      }
      this.error = null
      // Variable pour suivre si la requête a été annulée
      const wasCancelled = false
      try {
        // Vérifier si les données sont dans le cache
        const normalizedParams = normalizeParams(params)
        const cacheKey = this._generateCacheKey(portfolioId, normalizedParams)
        // Tenter de récupérer les données du cache
        const cachedData = this._getCachedData(cacheKey)
        if (cachedData) {
          return cachedData
        }
        // Si les données ne sont pas dans le cache, effectuer une requête au serveur
        let url = `/dividends/?portfolio_id=${portfolioId}`
        // Ajouter les paramètres à l'URL
        if (Object.keys(normalizedParams).length > 0) {
          const queryParams = this._buildQueryParams(normalizedParams)
          url += `&${queryParams.toString()}`
        }
        const response = await axios.get<Pagination>(url, {
          signal: controller.signal
        })
        // Mettre les données en cache
        this._setCachedData(cacheKey, response.data)
        // Mettre également en cache les données de base du portefeuille
        const portfolioCacheKey = `dividends_${portfolioId}`
        if (cacheKey !== portfolioCacheKey) {
          this._setCachedData(portfolioCacheKey, response.data)
        }
        return response.data
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`;
          // Marquer le portefeuille comme inexistant dans le portfolioStore
          const portfolioStore = usePortfolioStore();
          portfolioStore.cleanupPortfolioData(portfolioId);
        } else {
          this.error = getDividendErrorDetail(error, 'Erreur lors de la récupération des dividendes');
        }
        return [];
      } finally {
        if (!wasCancelled) {
          this.loading = false
          // Réinitialiser l'état de chargement spécifique
          switch (action) {
            case 'portfolio':
              this.isLoadingDividendsByPortfolio = false
              break
            case 'table':
              this.isLoadingDividendTableData = false
              break
            default:
              // Pour les autres actions, ne rien faire de spécial
              break
          }
          delete this.currentControllers[requestKey]
        }
      }
    },
    // 1. Fonction spécifique pour le changement de portefeuille
    async fetchDividendsByPortfolioChange(portfolioId: number, externalSignal?: AbortSignal): Promise<Pagination | Dividend[]> {
      return this._fetchDividendsBase(portfolioId, {}, externalSignal, 'portfolio')
    },
    // 2. Fonction spécifique pour le changement de page
    async fetchDividendsByPageChange(portfolioId: number, page: number, currentParams: DividendParams = {}, externalSignal?: AbortSignal): Promise<Pagination | Dividend[]> {
      const params = { ...currentParams, page }
      return this._fetchDividendsBase(portfolioId, params, externalSignal, 'page')
    },
    // 3. Fonction spécifique pour le changement de tri
    async fetchDividendsBySortChange(portfolioId: number, ordering: string, currentParams: DividendParams = {}, externalSignal?: AbortSignal): Promise<Pagination | Dividend[]> {
      const params = { ...currentParams, ordering }
      return this._fetchDividendsBase(portfolioId, params, externalSignal, 'sort')
    },
    // 4. Fonction spécifique pour l'application de filtres
    async fetchDividendsByPortfolio(portfolioId: number, params: DividendParams = {}): Promise<Pagination | Dividend[]> {
      // Annuler toute requête précédente
      if (this.portfolioFetchController) {
        this.portfolioFetchController.abort()
        this.portfolioFetchController = null
      }
      // Créer un nouveau contrôleur d'annulation
      this.portfolioFetchController = new AbortController()
      // Combiner avec le signal externe si fourni
      const signal = this.portfolioFetchController.signal
      // Définir l'état de chargement
      this.loading = true
      this.isLoadingDividendsByPortfolio = true
      this.error = null
      // Variable pour suivre si la requête a été annulée
      let wasCancelled = false
      try {
        // Appeler la fonction de base avec les paramètres appropriés
        return await this._fetchDividendsBase(portfolioId, params, signal, 'portfolio')
      } catch (error: unknown) {
        // Vérifier si l'erreur est due à une annulation
        if (isAbortError(error)) {
          wasCancelled = true
          return { count: 0, next: null, previous: null, results: [] }
        }
        this.error = getDividendErrorDetail(error, 'Erreur lors du chargement des dividendes')
        return { count: 0, next: null, previous: null, results: [] }
      } finally {
        if (!wasCancelled) {
          this.loading = false
          this.isLoadingDividendsByPortfolio = false
          this.portfolioFetchController = null
        }
      }
    },
    async updateDividend(id: number, dividendData: Partial<Dividend>): Promise<Dividend> {
      this.loading = true
      this.error = null
      try {
        const response = await axios.put<Dividend>(`/dividends/${id}/`, dividendData)
        // Mise à jour des données locales
        if (this.dividends.length > 0) {
          const index = this.dividends.findIndex(d => d.id === id)
          if (index !== -1) {
            this.dividends[index] = response.data
          }
        }
        // Invalider le cache pour ce portefeuille (utiliser la réponse qui contient 'portfolio')
        if (response.data?.portfolio) {
          this.invalidatePortfolioCache(response.data.portfolio)
        }
        return response.data;
      } catch (error: unknown) {
        this.error = getDividendErrorDetail(error, 'Erreur lors de la mise à jour du dividende')
        throw error
      } finally {
        this.loading = false
        // Réinitialiser tous les états de chargement spécifiques
        this.isLoadingDividendsByPortfolio = false
        this.isLoadingDividendTableData = false
      }
    },
    async createDividend(dividendData: DividendCreatePayload): Promise<Dividend> {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post<Dividend>('/dividends/', dividendData)
        // Mise à jour des données locales
        this.dividends.unshift(response.data)
        // Mise à jour des résultats de pagination si présents
        if (this.pagination.results && this.pagination.results.length > 0) {
          // Ajouter le nouveau dividende au début des résultats de pagination
          this.pagination.results.unshift(response.data)
          // Limiter la taille du tableau si nécessaire
          if (this.pagination.results.length > 10) { // Assuming pageSize is 10
            this.pagination.results.pop()
          }
          // Incrémenter le compteur total
          this.pagination.count++
        }
        // Invalider le cache pour ce portefeuille
        const createdPortfolioId = response.data?.portfolio ?? dividendData.portfolio_id;
        if (createdPortfolioId) {
          this.invalidatePortfolioCache(createdPortfolioId)
        }
        return response.data
      } catch (error: unknown) {
        this.error = getDividendErrorDetail(error, 'Erreur lors de la création du dividende')
        throw error
      } finally {
        this.loading = false
        // Réinitialiser tous les états de chargement spécifiques
        this.isLoadingDividendsByPortfolio = false
        this.isLoadingDividendTableData = false
      }
    },
    async deleteDividend(id: number, portfolioId?: number): Promise<AxiosResponse> {
      this.loading = true;
      this.error = null;
      // Sauvegarder l'état actuel pour pouvoir le restaurer en cas d'erreur
      const previousDividends = [...this.dividends];
      const previousPagination = JSON.parse(JSON.stringify(this.pagination));
      // Rechercher le portfolio_id du dividende pour l'invalidation ciblée du cache si non fourni
      if (!portfolioId) {
        try {
          const dividend = this.dividends.find(d => d.id === id);
          if (dividend) {
            portfolioId = dividend.portfolio;
          }
        } catch {
          /* ignored */
        }
      }
      // Mise à jour optimiste de l'état local
      this.dividends = this.dividends.filter(d => d.id !== id);
      // Mise à jour optimiste de la pagination si elle existe
      if (this.pagination && this.pagination.results) {
        this.pagination.results = this.pagination.results.filter(d => d.id !== id);
        // Mise à jour du compteur total
        if (this.pagination.count > 0) {
          this.pagination.count -= 1;
        }
      }
      try {
        // Envoyer la requête de suppression au serveur
        const response = await axios.delete(`/dividends/${id}/`);
        // Invalidation complète du cache pour tous les portefeuilles
        Object.keys(this.cache).forEach(key => {
          if (key.startsWith('dividends_')) {
            delete this.cache[key];
          }
        });
        // Invalidation ciblée du cache pour le portefeuille spécifique (pour plus de sécurité)
        if (portfolioId) {
          this.invalidatePortfolioCache(portfolioId);
        }
        return response;
      } catch (error: unknown) {
        // En cas d'erreur, restaurer l'état précédent
        this.dividends = previousDividends;
        this.pagination = previousPagination;
        this.error = getDividendErrorDetail(error, 'Erreur lors de la suppression du dividende');
        throw error;
      } finally {
        this.loading = false;
      }
    },
    invalidatePortfolioCache(portfolioId: number): void {
      const cacheKeys = Object.keys(this.cache)
      // Invalider toutes les clés liées à ce portefeuille (dividends, yearly_by_asset, dividend_yield, etc.)
      const       keysToInvalidate = cacheKeys.filter(key => 
        key.includes(`_${portfolioId}`) || 
        key.includes(`dividends_${portfolioId}`) ||
        key.includes(`yearly_by_asset_${portfolioId}`) ||
        key.includes(`per_share_by_year_${portfolioId}`) ||
        key.includes(`market_yield_by_asset_${portfolioId}`) ||
        key.includes(`dividend_yield_${portfolioId}`)
      )
      keysToInvalidate.forEach(key => {
        delete this.cache[key]
      })
      // Invalider aussi le cache des totaux de dividendes pour ce portefeuille
      if (this._totalDividendsCache[portfolioId]) {
        delete this._totalDividendsCache[portfolioId]
      }
    },
    // Méthode pour invalider une clé de cache spécifique
    invalidateCacheKey(key: string): void {
      if (this.cache[key]) {
        delete this.cache[key]
      }
    },
    // Méthode privée pour mettre en cache des données
    _setCachedData(key: string, data: DividendCacheValue): void {
      // Limiter la taille du cache
      const cacheKeys = Object.keys(this.cache)
      if (cacheKeys.length >= this.maxCacheEntries) {
        // Supprimer l'entrée la plus ancienne
        let oldestKey = cacheKeys[0]
        let oldestTimestamp = this.cache[oldestKey].timestamp
        for (const k of cacheKeys) {
          if (this.cache[k].timestamp < oldestTimestamp) {
            oldestKey = k
            oldestTimestamp = this.cache[k].timestamp
          }
        }
        delete this.cache[oldestKey]
      }
      // Ajouter les nouvelles données au cache
      this.cache[key] = {
        data,
        timestamp: Date.now()
      }
    },
    // Méthode privée pour récupérer des données du cache
    _getCachedData(key: string): DividendCacheValue | null {
      if (!this.cache[key]) {
        return null
      }
      const cacheItem = this.cache[key]
      const now = Date.now()
      // Vérifier si les données sont encore valides
      if (now - cacheItem.timestamp > this.cacheTTL) {
        // Les données sont expirées, les supprimer du cache
        delete this.cache[key]
        return null
      }
      return cacheItem.data
    },
    // Méthode pour récupérer des données du cache pour un portefeuille spécifique
    getCachedDividends(portfolioId: number, params: DividendParams = {}): DividendCacheValue | null {
      // Générer la clé de cache pour ces paramètres spécifiques
      const cacheKey = this._generateCacheKey(portfolioId, params)
      // Tenter de récupérer les données du cache avec les paramètres exacts
      const cachedData = this._getCachedData(cacheKey)
      if (cachedData) {
        return cachedData
      }
      // Si aucune donnée n'est trouvée dans le cache avec les paramètres exacts,
      // essayer de renvoyer les données du cache pour le portefeuille sans les filtres
      const portfolioCacheKey = `dividends_${portfolioId}`
      const portfolioCachedData = this._getCachedData(portfolioCacheKey)
      if (portfolioCachedData) {
        return portfolioCachedData
      }
      // Si aucune donnée n'est trouvée dans le cache, renvoyer null
      return null
    },
    // Méthode privée pour générer une clé de cache
    _generateCacheKey(portfolioId: number, params: DividendParams = {}): string {
      const normalizedParams = normalizeParams(params)
      // Créer une clé de base avec l'ID du portefeuille
      let cacheKey = `dividends_${portfolioId}`
      // Ajouter les paramètres à la clé de cache
      if (normalizedParams.page) {
        cacheKey += `_page_${normalizedParams.page}`
      }
      if (normalizedParams.ordering) {
        cacheKey += `_order_${normalizedParams.ordering}`
      }
      if (normalizedParams.date_from) {
        cacheKey += `_from_${normalizedParams.date_from}`
      }
      if (normalizedParams.date_to) {
        cacheKey += `_to_${normalizedParams.date_to}`
      }
      if (normalizedParams.asset) {
        cacheKey += `_asset_${normalizedParams.asset}`
      }
      if (normalizedParams.amount_min) {
        cacheKey += `_min_${normalizedParams.amount_min}`
      }
      if (normalizedParams.amount_max) {
        cacheKey += `_max_${normalizedParams.amount_max}`
      }
      return cacheKey
    },
    // Méthode privée pour construire les paramètres de requête
    _buildQueryParams(params: DividendParams = {}): URLSearchParams {
      const normalizedParams = normalizeParams(params)
      const queryParams = new URLSearchParams()
      // Paramètres de pagination
      if (normalizedParams.page) queryParams.append('page', normalizedParams.page.toString())
      if (normalizedParams.page_size) queryParams.append('page_size', normalizedParams.page_size.toString())
      // Paramètre de filtrage par portefeuille
      if (normalizedParams.portfolio_id) queryParams.append('portfolio_id', normalizedParams.portfolio_id.toString())
      // Paramètres de filtrage par date
      if (normalizedParams.date_from) queryParams.append('date_from', normalizedParams.date_from)
      if (normalizedParams.date_to) queryParams.append('date_to', normalizedParams.date_to)
      // Paramètres de filtrage par actif et montant
      if (normalizedParams.asset) queryParams.append('asset', normalizedParams.asset.toString())
      if (normalizedParams.amount_min) queryParams.append('amount_min', normalizedParams.amount_min.toString())
      if (normalizedParams.amount_max) queryParams.append('amount_max', normalizedParams.amount_max.toString())
      // Paramètre de tri
      if (normalizedParams.ordering) {
        queryParams.append('ordering', normalizedParams.ordering)
      }
      return queryParams
    },
    // Méthode pour annuler une requête précédente
    _abortPreviousRequest(requestKey: string): void {
      if (this.currentControllers[requestKey]) {
        try {
          // Ajouter un petit délai avant d'annuler pour éviter les annulations trop fréquentes
          // qui pourraient causer des problèmes d'affichage
          setTimeout(() => {
            if (this.currentControllers[requestKey]) {
              this.currentControllers[requestKey].abort()
              delete this.currentControllers[requestKey]
            }
          }, 50) // Délai de 50ms pour éviter les annulations trop rapides
        } catch {
          // Ignorer les erreurs d'annulation
          delete this.currentControllers[requestKey]
        }
      }
    },
    // Méthode pour récupérer le total des dividendes pour un portefeuille
    async fetchTotalDividends(portfolioId: number): Promise<number> {
      if (!portfolioId) return 0;
      // 1) Servir immédiatement depuis le cache si frais
      const cacheKey = portfolioId;
      const now = Date.now();
      const ttl = this.cacheTTL; // réutiliser le TTL global (5 min)
      const cached = this._totalDividendsCache?.[cacheKey];
      if (cached && (now - cached.timestamp) < ttl) {
        const cachedValue = Number(cached.data || 0);
        // Optionnel: ne pas déclencher d'événement si la valeur est identique pour éviter tout rerender inutile
        try {
          const portfolioStore = usePortfolioStore();
          const current = portfolioStore.currentPortfolio?.id === portfolioId
            ? portfolioStore.currentPortfolio?.total_dividends
            : (portfolioStore.getPortfolioById(portfolioId)?.total_dividends);
          if (Number(current ?? 0) !== cachedValue) {
            const event = new CustomEvent('dividend-total-updated', {
              detail: { portfolioId, totalDividends: cachedValue }
            });
            document.dispatchEvent(event);
          }
        } catch { /* ignored */ }
        return cachedValue;
      }
      // 2) Dedupe: si une requête est déjà en cours pour ce portefeuille, la réutiliser
      if (!this._totalDividendsInFlight) this._totalDividendsInFlight = new Map<number, Promise<number>>();
      const inflight = this._totalDividendsInFlight.get(portfolioId);
      if (inflight) {
        return await inflight;
      }
      const requestKey = `fetchTotalDividends_${portfolioId}`;
      this._abortPreviousRequest(requestKey);
      const controller = new AbortController();
      this.currentControllers[requestKey] = controller;
      this.loading = true;
      this.isLoadingTotalDividends = true;
      this.error = null;
      const req = axios.get(`/dividends/total_dividends/?portfolio_id=${portfolioId}`, {
        signal: controller.signal
      }).then((response) => {
        const totalDividends = Number(response?.data?.total_amount || 0);
        // Mettre en cache
        if (!this._totalDividendsCache) this._totalDividendsCache = {};
        this._totalDividendsCache[cacheKey] = { data: totalDividends, timestamp: Date.now() };
        // Déclencher un événement UNIQUEMENT si la valeur change
        try {
          const portfolioStore = usePortfolioStore();
          const current = portfolioStore.currentPortfolio?.id === portfolioId
            ? portfolioStore.currentPortfolio?.total_dividends
            : (portfolioStore.getPortfolioById(portfolioId)?.total_dividends);
          if (Number(current ?? 0) !== totalDividends) {
            const event = new CustomEvent('dividend-total-updated', {
              detail: { portfolioId, totalDividends }
            });
            document.dispatchEvent(event);
          }
        } catch { /* ignored */ }
        return totalDividends;
      }).catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`;
          // Marquer le portefeuille comme inexistant dans le portfolioStore
          const portfolioStore = usePortfolioStore();
          portfolioStore.cleanupPortfolioData(portfolioId);
        } else {
          this.error = getDividendErrorDetail(error, 'Erreur lors de la récupération du total des dividendes');
        }
        return 0;
      }).finally(() => {
        this.loading = false;
        this.isLoadingTotalDividends = false;
        delete this.currentControllers[requestKey];
        this._totalDividendsInFlight?.delete(portfolioId);
      });
      this._totalDividendsInFlight.set(portfolioId, req);
      return await req;
    },
    // Méthode pour récupérer les actifs éligibles aux dividendes pour un portefeuille
    async fetchEligibleAssets(portfolioId: number, allAssets: boolean = false): Promise<EligibleDividendAsset[]> {
      if (!portfolioId) return [];
      const requestKey = `fetchEligibleAssets_${portfolioId}`;
      this._abortPreviousRequest(requestKey);
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController();
      this.currentControllers[requestKey] = controller;
      this.loading = true;
      this.error = null;
      try {
        // Utiliser l'action eligible-dividend-assets du DividendViewSet avec le paramètre portfolio_id
        const allAssetsParam = allAssets ? '&all_assets=true' : '';
        const response = await axios.get(`/dividends/eligible-dividend-assets/?portfolio_id=${portfolioId}${allAssetsParam}`, {
          signal: controller.signal
        });
        return response.data as EligibleDividendAsset[];
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`;
          // Marquer le portefeuille comme inexistant dans le portfolioStore
          const portfolioStore = usePortfolioStore();
          portfolioStore.cleanupPortfolioData(portfolioId);
        } else {
          this.error = getDividendErrorDetail(error, 'Erreur lors de la récupération des actifs éligibles aux dividendes');
        }
        return [];
      } finally {
        this.loading = false;
        delete this.currentControllers[requestKey];
      }
    },
    // Méthode pour récupérer le rendement des dividendes par année pour un portefeuille
    async fetchDividendYields(portfolioId: number): Promise<DividendYieldByYear[]> {
      if (!portfolioId) return [];
      const requestKey = `fetchDividendYields_${portfolioId}`;
      this._abortPreviousRequest(requestKey);
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController();
      this.currentControllers[requestKey] = controller;
      this.loading = true;
      this.error = null;
      try {
        // Utiliser l'action dividend_yield du DividendViewSet avec le paramètre portfolio_id
        const response = await axios.get(`/dividends/dividend_yield/?portfolio_id=${portfolioId}`, {
          signal: controller.signal
        });
        return response.data as DividendYieldByYear[];
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`;
          // Marquer le portefeuille comme inexistant dans le portfolioStore
          const portfolioStore = usePortfolioStore();
          portfolioStore.cleanupPortfolioData(portfolioId);
        } else {
          this.error = getDividendErrorDetail(error, 'Erreur lors de la récupération du rendement des dividendes');
        }
        return [];
      } finally {
        this.loading = false;
        delete this.currentControllers[requestKey];
      }
    },
    // Méthode pour récupérer les dividendes agrégés par année pour un portefeuille
    async fetchDividendsByYear(portfolioId: number): Promise<DividendByYear[]> {
      if (!portfolioId) return [];
      // 1) Essayer le cache d'abord
      const cacheKey = `dividends_by_year_${portfolioId}`
      const cached = this._getCachedData(cacheKey)
      if (cached) return cached
      const requestKey = `fetchDividendsByYear_${portfolioId}`;
      this._abortPreviousRequest(requestKey);
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController();
      this.currentControllers[requestKey] = controller;
      this.loading = true;
      this.error = null;
      try {
        // Utiliser l'action by-year du DividendViewSet avec le paramètre portfolio_id
        const response = await axios.get(`/dividends/by-year/?portfolio_id=${portfolioId}`, {
          signal: controller.signal
        });
        // Mettre en cache la réponse
        this._setCachedData(cacheKey, response.data as DividendByYear[])
        return response.data as DividendByYear[];
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`;
          // Marquer le portefeuille comme inexistant dans le portfolioStore
          const portfolioStore = usePortfolioStore();
          portfolioStore.cleanupPortfolioData(portfolioId);
        } else {
          this.error = getDividendErrorDetail(error, 'Erreur lors de la récupération des dividendes par année');
        }
        return [];
      } finally {
        this.loading = false;
        delete this.currentControllers[requestKey];
      }
    },
    // Méthode optimisée pour récupérer le résumé des dividendes (total + max/min actifs)
    async fetchDividendsSummary(portfolioId: number): Promise<DividendsSummary> {
      if (!portfolioId) return { total_amount: 0, top_asset: null, bottom_asset: null }
      // 1) Essayer le cache d'abord
      const cacheKey = `dividends_summary_${portfolioId}`
      const cached = this._getCachedData(cacheKey)
      if (cached) return cached
      const requestKey = `fetchDividendsSummary_${portfolioId}`
      this._abortPreviousRequest(requestKey)
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController()
      this.currentControllers[requestKey] = controller
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`/dividends/summary/?portfolio_id=${portfolioId}`, {
          signal: controller.signal
        })
        // Mettre en cache la réponse
        this._setCachedData(cacheKey, response.data as DividendsSummary)
        // Déclencher l'événement pour mettre à jour total_dividends
        const totalDividends = Number(response.data.total_amount || 0)
        try {
          const portfolioStore = usePortfolioStore()
          const current = portfolioStore.currentPortfolio?.id === portfolioId
            ? portfolioStore.currentPortfolio?.total_dividends
            : (portfolioStore.getPortfolioById(portfolioId)?.total_dividends)
          if (Number(current ?? 0) !== totalDividends) {
            const event = new CustomEvent('dividend-total-updated', {
              detail: { portfolioId, totalDividends }
            })
            document.dispatchEvent(event)
          }
        } catch { /* ignored */ }
        return response.data as DividendsSummary
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
          const portfolioStore = usePortfolioStore()
          portfolioStore.cleanupPortfolioData(portfolioId)
        } else {
          this.error = getDividendErrorDetail(error, "Erreur lors de la récupération du résumé des dividendes")
        }
        return { total_amount: 0, top_asset: null, bottom_asset: null }
      } finally {
        this.loading = false
        delete this.currentControllers[requestKey]
      }
    },
    /** Récupère tous les dividendes d'un portefeuille (pagination complète). */
    async fetchAllDividendsForPortfolio(
      portfolioId: number,
      params: DividendParams = {}
    ): Promise<Dividend[]> {
      if (!portfolioId) return []
      const baseParams: DividendParams = {
        ...params,
        portfolio_id: portfolioId,
        page_size: 50,
        ordering: params.ordering ?? '-date',
      }
      const allDividends: Dividend[] = []
      let page = 1
      while (true) {
        const response = await this._fetchDividendsBase(
          portfolioId,
          { ...baseParams, page },
          undefined,
          'all_pages'
        )
        const pageData = response as Pagination
        const results = Array.isArray(response)
          ? response
          : (pageData?.results || [])
        if (!results.length) {
          break
        }
        allDividends.push(...results)
        if (!pageData?.next) {
          break
        }
        page += 1
      }
      return allDividends
    },
    // Moyenne du dividende par titre par année et par actif (graphique évolution)
    async fetchPerShareDividendsByYearAsset(portfolioId: number): Promise<PerShareDividendByYearAsset[]> {
      if (!portfolioId) return []
      const cacheKey = `per_share_by_year_${portfolioId}`
      const cached = this._getCachedData(cacheKey)
      if (cached) return cached
      const requestKey = `fetchPerShareByYear_${portfolioId}`
      this._abortPreviousRequest(requestKey)
      const controller = new AbortController()
      this.currentControllers[requestKey] = controller
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(
          `/dividends/per_share_by_year_asset/?portfolio_id=${portfolioId}`,
          { signal: controller.signal }
        )
        const rows = Array.isArray(response.data) ? response.data as PerShareDividendByYearAsset[] : []
        if (rows.length > 0) {
          this._setCachedData(cacheKey, rows)
        } else {
          delete this.cache[cacheKey]
        }
        return rows
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
          const portfolioStore = usePortfolioStore()
          portfolioStore.cleanupPortfolioData(portfolioId)
        } else {
          this.error = getDividendErrorDetail(
            error,
            "Erreur lors de la récupération des dividendes par titre par année"
          )
        }
        return []
      } finally {
        this.loading = false
        delete this.currentControllers[requestKey]
      }
    },
    // Méthode pour récupérer les dividendes agrégés par année et par actif (treemap)
    async fetchYearlyDividendsByAsset(portfolioId: number): Promise<YearlyDividendByAsset[]> {
      if (!portfolioId) return []
      // 1) Essayer le cache d'abord
      const cacheKey = `yearly_by_asset_${portfolioId}`
      const cached = this._getCachedData(cacheKey)
      if (cached) return cached
      const requestKey = `fetchYearlyByAsset_${portfolioId}`
      this._abortPreviousRequest(requestKey)
      // Créer un nouveau controller pour cette requête
      const controller = new AbortController()
      this.currentControllers[requestKey] = controller
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`/dividends/yearly_by_asset/?portfolio_id=${portfolioId}`, {
          signal: controller.signal
        })
        // Mettre en cache la réponse
        this._setCachedData(cacheKey, response.data as YearlyDividendByAsset[])
        return response.data as YearlyDividendByAsset[]
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
          const portfolioStore = usePortfolioStore()
          portfolioStore.cleanupPortfolioData(portfolioId)
        } else {
          this.error = getDividendErrorDetail(
            error,
            "Erreur lors de la récupération des dividendes par actif et par année"
          )
        }
        return []
      } finally {
        this.loading = false
        delete this.currentControllers[requestKey]
      }
    },
    async fetchMarketYieldByAssetYear(portfolioId: number): Promise<MarketYieldByAssetYear[]> {
      if (!portfolioId) return []
      const cacheKey = `market_yield_by_asset_${portfolioId}`
      const cached = this._getCachedData(cacheKey)
      if (cached) return cached
      const requestKey = `fetchMarketYieldByAssetYear_${portfolioId}`
      this._abortPreviousRequest(requestKey)
      const controller = new AbortController()
      this.currentControllers[requestKey] = controller
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(
          `/dividends/market_yield_by_asset_year/?portfolio_id=${portfolioId}`,
          { signal: controller.signal }
        )
        this._setCachedData(cacheKey, response.data as MarketYieldByAssetYear[])
        return response.data as MarketYieldByAssetYear[]
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          this.error = `Le portefeuille avec l'ID ${portfolioId} n'existe pas ou a été supprimé`
          const portfolioStore = usePortfolioStore()
          portfolioStore.cleanupPortfolioData(portfolioId)
        } else {
          this.error = getDividendErrorDetail(
            error,
            'Erreur lors de la récupération du rendement dividende sur cours'
          )
        }
        return []
      } finally {
        this.loading = false
        delete this.currentControllers[requestKey]
      }
    }
  }
})
